import { useEffect } from 'react';
import { OPT_OUT_GROUPS_MAP } from '@bridebook/web/app-shared/lib/app/constants';
import { AnalyticsConsentWindow } from '@bridebook/web/app-shared/lib/app/types';

const OPT_OUT_COOKIE_BASE = 'OptOutOnRequest=groups=';
// Function to set the MetaRouter cookie based on consent data
const setMetaRouterOptOutCookie = (cmpData: any) => {
  const consentString = Object.values(OPT_OUT_GROUPS_MAP)
    .map((id) => {
      //ensure that strictly necessary cookies (C0001 -> id === 1) cannot be opted out
      const value = id === 1 || cmpData.purposeLI?.[`c5${id}`] ? 1 : 0;
      const category = `C000${id}`;
      return `${category}:${value}`;
    })
    .join(',');

  document.cookie = `${OPT_OUT_COOKIE_BASE}${encodeURIComponent(consentString)};path=/`;
  const win = window as AnalyticsConsentWindow;
  // Refresh analytics compliance if available
  win.analytics?.refreshCompliance?.();
};

/**
 * Initialize the default opt-out cookie value
 * - If the cookie already exists, ensure that strictly necessary cookies (C0001) cannot be opted out
 * - If the cookie does not exist, set all categories to be allowed by default
 */
const initializeOptOutCookie = () => {
  const cookie = document.cookie.split('; ').find((c) => c.startsWith(OPT_OUT_COOKIE_BASE));

  let groups: string[];

  if (cookie) {
    const value = decodeURIComponent(cookie.split(OPT_OUT_COOKIE_BASE)[1] ?? '');

    // Remove any variant of C0001:* and enforce C0001:1 at the front
    groups = value
      .split(',')
      .map((group) => group.trim())
      .filter((group) => group && !group.startsWith('C0001:'));
    groups.unshift('C0001:1');
  } else {
    groups = Object.values(OPT_OUT_GROUPS_MAP).map((id) => `C000${id}:1`);
  }

  document.cookie = `${OPT_OUT_COOKIE_BASE}${encodeURIComponent(groups.join(','))};path=/`;

  const win = window as AnalyticsConsentWindow;
  // Refresh analytics compliance if available
  win.analytics?.refreshCompliance?.();
};

// Custom hook to handle MetaRouter cookie setup
export const useMetaRouterOptOutCookie = () => {
  useEffect(() => {
    initializeOptOutCookie();
    const win = window as AnalyticsConsentWindow;
    if (win.__cmp) {
      const handleConsentScreenOff = () => {
        const cmpData = win.__cmp?.('getCMPData');
        if (cmpData) {
          setMetaRouterOptOutCookie(cmpData);
        } else {
          // eslint-disable-next-line no-console
          console.error('CMP Data not available');
        }
      };
      win.__cmp('addEventListener', ['consentscreenoff', handleConsentScreenOff, false], null);

      return () => {
        win.__cmp?.(
          'removeEventListener',
          ['consentscreenoff', handleConsentScreenOff, false],
          null,
        );
      };
    }
  }, []);
};
